// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"bc4eca7917aa04c6556e565679396154f2c0d72b"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = "";globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[],\"staticRoutes\":[]}";import * as Sentry from '@sentry/nextjs';

import { shouldDropErrorEvent, UPPBEAT_SCRIPT_URL } from '@/lib/errorFilter';
import { isClientEventBatchFailure, logEvent } from '@/pages/api/v1/utils';

Sentry.init({
  dsn: 'https://f4d8fc15c0874470b9e7b155d0777b10@o4505391192211456.ingest.sentry.io/4505391200731136',

  // Sample 1% of transactions.
  tracesSampleRate: 0.01,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,
  environment: process.env.NEXT_PUBLIC_ENV,
  allowUrls: [UPPBEAT_SCRIPT_URL],
  integrations: [
    Sentry.browserTracingIntegration({
      enableInp: true,
    }),
  ],
  beforeSend: (event) => {
    const exception = event.exception?.values?.[0];

    // Drop non-actionable browser/environment noise before logging (DEV-5936).
    // Returning null discards the Sentry event and skips the ClickHouse log below.
    if (
      shouldDropErrorEvent({
        message: exception?.value ?? event.message ?? '',
        type: exception?.type,
      })
    ) {
      return null;
    }

    // Don't feed event-pipeline failures back into the event pipeline, or a
    // failing /api/v1/event keeps re-reporting itself (UPPBEAT-5ZM).
    if (
      (event.exception || event.message) &&
      !isClientEventBatchFailure(event)
    ) {
      logEvent({
        event_type: 'error',
        asPath: window?.location?.pathname ?? 'unknown',
        metadata: {
          text: exception?.value ?? event.message ?? 'unknown',
        },
      }).catch(() => {
        // Ignore logEvent errors to prevent infinite loops
      });
    }

    return event;
  },
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    'Script error.',
    // Avast extension error
    '_avast_submit',

    // Custom errors.
    'AbortError: The play() request was interrupted by a call to pause(). https://goo.gl/LdLk22',
    'ResizeObserver loop limit exceeded',
    "The fetching process for the media resource was aborted by the user agent at the user's request.",
    'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',
    'AbortError: The operation was aborted.',
    'Unexpected end of JSON input',
    'SyntaxError: Unexpected end of JSON input',
    'ResizeObserver loop completed with undelivered notifications.',
    'Failed to load Stripe.js',
    "null is not an object (evaluating 'this.backend.isPaused')",
    'Load failed',
    /No router instance found.*/s,
  ],
  denyUrls: [
    // Google Adsense
    /pagead\/js/i,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
});
